"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";

import { BindableInput } from "./BindableInput";
import { File as SpotFile } from "@/types";
import { FileUpload } from "@/components/Form/FileInputField";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { DataTypeBadge } from "@/icons/DataTypeIcon";
import { cn } from "@/lib/utils";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";

/**
 * Long MCP-tool descriptions (several sentences) blow up the form. Keep the
 * first two sentences inline and hide the rest behind a tooltip. Sentence split
 * is a deliberately rough `[.!?]` heuristic — worst case it clamps a touch off,
 * and the full text is always in the tooltip.
 */
function clampToTwoSentences(text: string): { short: string; full?: string } {
  const sentences = text.match(/[^.!?]+[.!?]+/g);
  if (!sentences || sentences.length <= 2) return { short: text };
  return { short: `${sentences.slice(0, 2).join("").trim()} …`, full: text };
}

function RowDescription({ text }: { text: string }) {
  const { short, full } = clampToTwoSentences(text);
  if (!full) {
    return (
      <p className="text-xs leading-snug text-muted-foreground">{short}</p>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className="cursor-help text-xs leading-snug text-muted-foreground">
          {short}
        </p>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-w-xs whitespace-normal text-left leading-normal"
      >
        {full}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * V2 renderer for `display=ConfigBindableDictRows`. A user-named map where
 * each row's value is a literal or a `:var[...]` ref. The picker affordance
 * is inherited from `BindableInput`; row name + add/remove is local UI.
 *
 * `lockedRows` (from the config endpoint) fixes the row keys; the user only
 * edits values. Used by InvokeSubflowV2Node's per-input rows — there each row
 * carries the subflow input's `type` and `is_list`, so a File input renders a
 * file picker (and the variable picker filters to File sources) rather than a
 * text box.
 * Rows may also carry `optional` (shown as a muted hint; backend validation
 * lets them stay empty) and `default` — a fixed-value input's constant, shown
 * prefilled until the user writes their own value (an untouched row stays out
 * of node config, so the child falls back to its constant at runtime).
 */

interface LockedRow {
  key: string;
  label: string;
  type?: string;
  is_list?: boolean;
  editor?: "key_value";
  key_label?: string;
  value_label?: string;
  optional?: boolean;
  default?: unknown;
  description?: string;
  placeholder?: string;
  enum?: Array<string | { value: string; label: string }>;
}

/** Controlled key/value editor used by dictionary-valued plugin settings. */
function KeyValueRowsEditor({
  nodeId,
  value,
  keyLabel = "Key",
  valueLabel = "Value",
  disabled,
  onChange,
}: {
  nodeId: string;
  value: unknown;
  keyLabel?: string;
  valueLabel?: string;
  disabled?: boolean;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const rows: Record<string, unknown> =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};

  const setRow = (name: string, next: unknown) =>
    onChange({ ...rows, [name]: next });

  const renameRow = (oldName: string, newName: string) => {
    if (!newName || newName === oldName || rows[newName] !== undefined) return;
    const next: Record<string, unknown> = {};
    for (const [key, rowValue] of Object.entries(rows)) {
      next[key === oldName ? newName : key] = rowValue;
    }
    onChange(next);
  };

  const removeRow = (name: string) => {
    const next = { ...rows };
    delete next[name];
    onChange(next);
  };

  const addRow = () => {
    let index = Object.keys(rows).length + 1;
    while (rows[`Variable ${index}`] !== undefined) index += 1;
    onChange({ ...rows, [`Variable ${index}`]: "" });
  };

  return (
    <div className="flex flex-col gap-2">
      {Object.entries(rows).map(([name, rowValue]) => (
        <div key={name} className="flex flex-col gap-1.5 rounded-md border p-2">
          <div className="flex items-center gap-1.5">
            <Input
              aria-label={keyLabel}
              defaultValue={name}
              placeholder={keyLabel}
              disabled={disabled}
              className="h-8 min-w-0 flex-1 bg-background"
              onBlur={(event) => renameRow(name, event.target.value.trim())}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove ${name}`}
              disabled={disabled}
              className="size-8 shrink-0 text-muted-foreground"
              onClick={() => removeRow(name)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          <BindableInput
            nodeId={nodeId}
            dataType="str"
            value={
              typeof rowValue === "string"
                ? rowValue
                : rowValue == null
                  ? ""
                  : String(rowValue)
            }
            placeholder={valueLabel}
            disabled={disabled}
            onChange={(next) => setRow(name, next)}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        className="w-full"
        onClick={addRow}
      >
        Add variable
        <Plus size="1.25rem" strokeWidth="1.25px" />
      </Button>
    </div>
  );
}

/**
 * One row's value editor, dispatched on the row's data `type`. File rows get
 * the standard upload/library picker as the literal (with the bind-to-variable
 * braces layered on by {@link BindableInput}); other list rows have no literal
 * editor and must bind a variable; every other type is a single bindable input
 * whose picker is filtered to compatible sources via `dataType`.
 * Storage per row: a `:var[...]` ref string, a literal string, or SpotFile(s).
 */
function RowValueEditor({
  nodeId,
  type,
  isList,
  editor,
  keyLabel,
  valueLabel,
  value,
  placeholder,
  enumOptions,
  disabled,
  onChange,
}: {
  nodeId: string;
  type?: string;
  isList?: boolean;
  editor?: "key_value";
  keyLabel?: string;
  valueLabel?: string;
  value: unknown;
  placeholder?: string;
  enumOptions?: Array<string | { value: string; label: string }>;
  disabled?: boolean;
  onChange: (next: unknown) => void;
}) {
  const isRef = typeof value === "string" && WHOLE_VAR_REF_PATTERN.test(value);

  if (editor === "key_value") {
    return (
      <KeyValueRowsEditor
        nodeId={nodeId}
        value={value}
        keyLabel={keyLabel}
        valueLabel={valueLabel}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }

  if (type?.toLowerCase() === "file") {
    const values: SpotFile[] =
      !isRef && value && typeof value === "object"
        ? Array.isArray(value)
          ? (value as SpotFile[])
          : "uri" in value
            ? [value as SpotFile]
            : []
        : [];
    return (
      <BindableInput
        nodeId={nodeId}
        dataType="File"
        isList={isList}
        value={isRef ? (value as string) : ""}
        disabled={disabled}
        renderLiteral={
          <FileUpload
            variant="inline"
            values={values}
            isDisabled={disabled}
            canSelectMultipleFiles={!!isList}
            showSelectedFiles
            setValues={(files) => onChange(isList ? files : (files[0] ?? null))}
          />
        }
        onChange={(next) => onChange(next)}
      />
    );
  }

  if (isList) {
    return (
      <BindableInput
        nodeId={nodeId}
        dataType={type}
        isList
        variableOnly
        value={isRef ? (value as string) : ""}
        disabled={disabled}
        onChange={(next) => onChange(next)}
      />
    );
  }

  const literal =
    typeof value === "string"
      ? value
      : typeof value === "number" || typeof value === "boolean"
        ? String(value)
        : "";
  return (
    <BindableInput
      nodeId={nodeId}
      dataType={type}
      value={literal}
      placeholder={placeholder}
      enumOptions={enumOptions}
      disabled={disabled}
      onChange={(next) => onChange(next)}
    />
  );
}

export function BindableDictRowsField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  isRequired,
  showOptionalLabel,
  error,
  lockedRows,
  emptyMessage,
}: {
  nodeId: string;
  fieldKey: string;
  label?: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  lockedRows?: LockedRow[];
  emptyMessage?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as Record<string, unknown> | undefined;
  const rows: Record<string, unknown> = useMemo(
    () => (raw && typeof raw === "object" ? raw : {}),
    [raw]
  );
  const locked = lockedRows !== undefined;
  const entries: LockedRow[] = locked
    ? lockedRows
    : Object.keys(rows).map((k) => ({ key: k, label: k }));
  const invalid = !!error;

  const write = (next: Record<string, unknown>) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  const setRow = (name: string, next: unknown) =>
    write({ ...rows, [name]: next });

  const renameRow = (oldName: string, newName: string) => {
    if (!newName || newName === oldName || rows[newName] !== undefined) return;
    const next: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rows))
      next[k === oldName ? newName : k] = v;
    write(next);
  };

  const removeRow = (name: string) => {
    const next = { ...rows };
    delete next[name];
    write(next);
  };

  const addRow = () => {
    let i = entries.length + 1;
    while (rows[`Input ${i}`] !== undefined) i += 1;
    write({ ...rows, [`Input ${i}`]: "" });
  };

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
          isRequired={isRequired}
          showOptionalLabel={showOptionalLabel}
        />
      ) : null}
      {description ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <div
        className={cn(
          "flex w-full flex-col gap-6",
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70 p-1.5"
        )}
      >
        {locked && entries.length === 0 && (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            {emptyMessage ?? "No fields to fill in."}
          </div>
        )}
        {entries.map(
          ({
            key,
            label: rowLabel,
            type: rowType,
            is_list: rowIsList,
            editor: rowEditor,
            key_label: rowKeyLabel,
            value_label: rowValueLabel,
            optional: rowOptional,
            default: rowDefault,
            description: rowDescription,
            placeholder: rowPlaceholder,
            enum: rowEnum,
          }) => (
            <div
              key={key}
              className="flex flex-col gap-1.5"
              data-testid="dict-row"
              data-row-key={key}
            >
              <div className="flex items-center justify-between gap-2">
                {locked ? (
                  <span
                    className="min-w-0 truncate text-sm font-medium"
                    title={rowLabel}
                  >
                    {rowLabel}
                  </span>
                ) : (
                  <Input
                    disabled={disabled}
                    defaultValue={key}
                    className="h-8 min-w-0 flex-1 bg-background"
                    onBlur={(e) => renameRow(key, e.target.value.trim())}
                  />
                )}
                <div className="flex shrink-0 items-center gap-1.5">
                  {rowOptional && rowDefault === undefined ? (
                    <span className="text-xs font-normal leading-none text-muted-foreground">
                      Optional
                    </span>
                  ) : null}
                  {rowType ? (
                    <DataTypeBadge dataType={rowType} isList={rowIsList} />
                  ) : null}
                  {!locked && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${key}`}
                      disabled={disabled}
                      className="size-8 text-muted-foreground"
                      onClick={() => removeRow(key)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </div>
              <RowValueEditor
                nodeId={nodeId}
                type={rowType}
                isList={rowIsList}
                editor={rowEditor}
                keyLabel={rowKeyLabel}
                valueLabel={rowValueLabel}
                value={rows[key] !== undefined ? rows[key] : rowDefault}
                placeholder={rowPlaceholder}
                enumOptions={rowEnum}
                disabled={disabled}
                onChange={(next) => setRow(key, next)}
              />
              {rowDescription ? <RowDescription text={rowDescription} /> : null}
            </div>
          )
        )}
      </div>
      {!locked && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full"
          onClick={addRow}
        >
          Add variable
          <Plus size="1.25rem" strokeWidth="1.25px" />
        </Button>
      )}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}
